import Cookies from "js-cookie";

/**
 * Matches the Snowplow first-party ID cookie, whose suffix is a hash of the
 * collector domain — e.g. `_sp_id.e981` on pivothealth.com.
 *
 * Kept byte-identical to the pattern `dtc-agent` uses (`get-snowplow-cookie.ts`)
 * so both edges into `POST /call/campaign` pick the same cookie.
 */
const SP_ID_COOKIE_PATTERN = /^_sp_id\.[a-f0-9]+/;

/**
 * Read the raw Snowplow ID cookie, the carrier for `domain_userid` and
 * `domain_sessionid`.
 *
 * SEM Content parses this cookie out of the `snowplow-data` header — splitting on
 * `.` and taking index 0 as `domain_userid` and index 5 as `domain_sessionid`
 * (`SnowplowParser.parseSnowplowData`) — then puts both onto the Invoca
 * `allocate_number.json` query (`InvocaServiceImpl.getPhoneCallCampaign`). Without
 * the header the Invoca record cannot be joined back to a Snowplow session, so the
 * call is unattributed (PPI-1802).
 *
 * `domain_userid` alone has a second, higher-priority source: the request body
 * field of the same name, which `SnowplowServiceImpl.getSnowplowData` uses to
 * OVERRIDE whatever the header parsed. `domain_sessionid` has no body equivalent —
 * the header is its only source, which is why this cookie is load-bearing.
 *
 * Returns `null` when Snowplow has not written the cookie yet, or when it is
 * present but empty.
 */
export function getSnowplowCookie(): string | null {
  // js-cookie returns `undefined` when there is no `document` (SSR), while typing
  // itself as always returning an object — `get(): { [key: string]: string }`. The
  // annotation below is what makes this guard legal to `strict` mode; without it
  // the `Object.keys` that follows is an unguarded `TypeError`. Unreachable today,
  // since the only caller runs inside `useEffect` and `DynamicPhone` is registered
  // `ssr: false`, but the parity with `dtc-agent` claimed above is a contract, not
  // just a shared regex — and `readCookie` guards there.
  const all: Record<string, string> | undefined = Cookies.get();
  if (!all) return null;

  const name = Object.keys(all).find((key) => SP_ID_COOKIE_PATTERN.test(key));
  return (name && all[name]) || null;
}
